import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { Entity, Pagination, VehicleHistoricEntry, VehicleHistoricFilter } from '@core/models';
import { SelectOption } from '../../design-system/components/listbox/listbox.component';
import { VehicleHistoricType } from '@core/constants/vehicle-historic-type.enum';
import { TranslateService } from '@ngx-translate/core';
import { getAngularService } from '../../ng-upgrade.utils';
import { DialogService } from '../../services/dialog.service';
import { ToastService } from '../../services/toast.service';
import { VehicleChangesHistoryService } from '../../services/vehicle-changes-history.service';

export const VEHICLE_HISTORY_DETAILS_DIALOG_SELECTOR = 'app-vehicle-history-details-dialog';

const DETAILABLE_CHANGE_TYPES: ReadonlySet<number> = new Set<number>([
  VehicleHistoricType.Value,
  VehicleHistoricType.Create,
  VehicleHistoricType.Delete,
  VehicleHistoricType.Update,
]);

function toChangeTypeCode(value: VehicleHistoricType | number | string | undefined): number {
  return Number(value);
}

const DEFAULT_PAGE_SIZE = 10;

const CARD_SURFACE_CLASS = 'rounded-aav-sm border-[1px] border-aav-neutral-200 bg-aav-surface';

const RESULTS_CARD_CLASS = 'overflow-hidden ' + CARD_SURFACE_CLASS;

const PAGE_SIZE_OPTIONS: ReadonlyArray<number> = [10, 25, 50, 100];

// Barra de skeleton (mesmo padrao do dashboard: cinza neutro com animate-aav-pulse). Sem
// altura nem largura proprias — cada uso declara as suas, porque duas classes h-[...] no
// mesmo elemento dependeriam da ordem das regras na folha gerada, nao da ordem no atributo.
// Toda barra de texto fica dentro de um wrapper com a altura da caixa de linha do texto
// real que ela substitui: sem o wrapper a barra (mais baixa que a linha) encolheria o bloco
// e a tela saltaria de altura ao trocar loading -> conteudo.
const SKELETON_BAR_CLASS = 'rounded bg-aav-neutral-200 animate-aav-pulse';

// Placeholder dos 3 campos do painel de filtro e do botao Pesquisar: 38px e a altura de
// aav-text-field/aav-listbox size="dense" e de aav-button size padrao (md).
const SKELETON_FIELD_CLASS = 'h-[38px] rounded-aav-sm bg-aav-neutral-200 animate-aav-pulse';

// O botao Pesquisar e pill (rounded-full em aav-button), nao arredondado como os campos.
const SKELETON_SUBMIT_CLASS =
  'h-[38px] w-[11.5rem] rounded-full bg-aav-neutral-200 animate-aav-pulse';

// Placeholder do seletor de itens por pagina (30px de altura, igual ao <select> real).
const SKELETON_PAGE_SIZE_CLASS =
  'h-[30px] w-16 rounded-aav-xs bg-aav-neutral-200 animate-aav-pulse';

// Placeholder de um botao de paginacao — 30x30 e radius xs sao a geometria de
// .evo-pagination-btn em shared/components/pagination/pagination.component.ts.
const SKELETON_PAGE_BUTTON_CLASS =
  'h-[30px] w-[30px] rounded-aav-xs bg-aav-neutral-200 animate-aav-pulse';

// Espelha maxRenderedPages de PaginationComponent: a janela de numeros que ele desenha
// entre os botoes anterior/proximo. Se aquele valor mudar, o skeleton desenha um botao a
// mais ou a menos que a paginacao real.
const PAGINATION_MAX_RENDERED_PAGES = 5;

function buildPaginationSkeletonButtons(totalPages: number): ReadonlyArray<number> {
  if (totalPages <= 1) return [];
  return buildSkeletonRows(Math.min(totalPages, PAGINATION_MAX_RENDERED_PAGES) + 2);
}

// Uma largura por coluna de columnKeys, na mesma ordem — aproxima o comprimento tipico do
// dado real (data, entidade, placa, tipo, valor antigo, valor novo).
const SKELETON_CELL_WIDTH_CLASSES: ReadonlyArray<string> = [
  'w-[7.5rem]',
  'w-[8.75rem]',
  'w-[4.5rem]',
  'w-[6rem]',
  'w-[9rem]',
  'w-[9rem]',
];

function buildSkeletonRows(size: number): ReadonlyArray<number> {
  return Array.from({ length: size }, (_, index) => index);
}

@Component({
  selector: 'app-vehicle-historic-v2',
  template: `
    <!-- Classes com valor arbitrário (ex.: px-[1.5rem], border-[1px]) substituem o utility
         bare equivalente do Tailwind (ex.: px-6, border) porque o CSS legado
         (assets/css/style.comp.css) declara essas classes com !important e valor
         diferente do pretendido — ver BRIEF-004. O guard
         src-angular/app/design-system/legacy-css-collisions.spec.ts garante que nenhum
         utility bare colidente volte a esta tela. Espaçamento em rem (não px) preserva a
         escala de acessibilidade (zoom de fonte do navegador); border-[1px] fica em px
         porque largura de borda não escala com rem por convenção. O guard só tokeniza
         atributos class="..." literais e constantes TS — classes ligadas via
         [ngClass]/[class] com string inline não são varridas; revisão manual continua
         necessária para essas. -->
    <div class="aav-scope view-wrapper view-wrapper__dashboard">
      <div class="container">
        <div class="col-md-12">
          <!-- As alturas 28px e 20px sao as caixas de linha de text-aav-title-large-strong
               e text-aav-body-medium (lineHeight dos tokens em tailwind.config.js) — o
               cabecalho ocupa o mesmo espaco no skeleton e no conteudo real. -->
          <div class="view-title mb-[1.25rem]" *ngIf="initialLoading" data-state="loading">
            <div class="flex h-[28px] items-center">
              <div [class]="skeletonBarClass + ' h-[1rem] w-[22rem] max-w-full'"></div>
            </div>
            <div class="mt-[6px] flex h-[20px] items-center">
              <div [class]="skeletonBarClass + ' h-[0.75rem] w-[17rem] max-w-full'"></div>
            </div>
          </div>

          <div class="view-title mb-[1.25rem]" *ngIf="!initialLoading">
            <h1 class="m-0 text-aav-title-large-strong text-aav-on-surface">
              {{ 'VEHICLE_HISTORIC.TITLE' | translate }}
            </h1>
            <p class="mt-[6px] mb-0 text-aav-body-medium text-aav-on-surface-variant">
              {{ 'VEHICLE_HISTORIC.SUB_TITLE' | translate }}
            </p>
          </div>

          <div class="flex flex-col gap-[1.25rem]">
            <!-- Espelha a geometria de aav-filter-panel (mesmo card, mesmo grid de 3
                 colunas + coluna auto do botao, mesmo gap) em vez de skeletonizar por
                 dentro do componente de design system, que e compartilhado com outras
                 telas. -->
            <section
              *ngIf="initialLoading"
              data-state="loading"
              data-block="filter-panel-skeleton"
              class="rounded-aav-sm border-[1px] border-solid border-aav-neutral-200 bg-aav-surface px-[1.25rem] py-[0.875rem]"
            >
              <div class="mb-[1rem] flex h-[24px] items-center">
                <div [class]="skeletonBarClass + ' h-[0.875rem] w-[11rem] max-w-full'"></div>
              </div>

              <div
                class="grid items-end gap-[0.875rem]"
                style="grid-template-columns: repeat(3, minmax(0, 1fr)) auto"
              >
                <div [class]="skeletonFieldClass"></div>
                <div [class]="skeletonFieldClass"></div>
                <div [class]="skeletonFieldClass"></div>
                <div [class]="skeletonSubmitClass"></div>
              </div>
            </section>

            <form *ngIf="!initialLoading" [formGroup]="filterForm" (ngSubmit)="search()">
              <aav-filter-panel
                [heading]="'VEHICLE_HISTORIC.FILTER_BOX.TITLE' | translate"
                [submitLabel]="'VEHICLE_HISTORIC.FILTER_BOX.BUTTON_SEARCH' | translate"
                [submitLoading]="loading"
                [columns]="3"
              >
                <aav-text-field
                  type="date"
                  size="dense"
                  leadingIcon="event"
                  formControlName="date"
                  data-filter="date"
                  [label]="'VEHICLE_HISTORIC.FILTER_BOX.DATE' | translate"
                ></aav-text-field>

                <aav-listbox
                  class="w-full"
                  shape="dense"
                  formControlName="entityId"
                  data-filter="entity"
                  [options]="entityOptions"
                  [label]="'VEHICLE_HISTORIC.FILTER_BOX.ENTITY' | translate"
                  [ariaLabel]="'VEHICLE_HISTORIC.FILTER_BOX.ENTITY' | translate"
                ></aav-listbox>

                <aav-text-field
                  size="dense"
                  formControlName="plate"
                  data-filter="plate"
                  [maxlength]="8"
                  [uppercase]="true"
                  [label]="'VEHICLE_HISTORIC.FILTER_BOX.PLATE' | translate"
                  [placeholder]="'VEHICLE_HISTORIC.FILTER_BOX.PLATE' | translate"
                ></aav-text-field>
              </aav-filter-panel>
            </form>

            <!-- Tabela -->
            <div [class]="RESULTS_CARD_CLASS">
              <div
                class="flex flex-wrap items-center justify-between gap-[0.625rem] bg-aav-surface-container-lowest px-[1.5rem] py-[0.875rem]"
                data-info="results-title"
              >
                <h4 class="m-0 text-[13px] font-bold text-aav-on-surface">
                  {{ 'VEHICLE_HISTORIC.SEARCH_RESULTS_TITLE' | translate }}
                </h4>
              </div>

              <div class="overflow-x-auto">
                <table
                  class="w-full min-w-[560px] border-collapse text-[13px]"
                  [attr.aria-busy]="loading || null"
                >
                  <caption *ngIf="loading" class="sr-only" role="status" aria-live="polite">
                    {{
                      'VEHICLE_HISTORIC.LOADING' | translate
                    }}
                  </caption>
                  <thead>
                    <tr
                      class="border-b border-l-0 border-r-0 border-t-0 border-solid border-aav-neutral-200 text-left"
                    >
                      <th
                        *ngFor="let columnKey of columnKeys"
                        class="whitespace-nowrap px-[1.5rem] py-[1rem] font-bold text-aav-on-surface"
                      >
                        {{ columnKey | translate }}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <ng-container *ngIf="loading">
                      <tr
                        *ngFor="let row of skeletonRows; trackBy: trackBySkeletonRow"
                        data-state="loading"
                        class="border-b border-l-0 border-r-0 border-t-0 border-solid border-aav-outline"
                      >
                        <td
                          *ngFor="
                            let widthClass of skeletonCellWidthClasses;
                            trackBy: trackBySkeletonCell
                          "
                          class="px-[1.5rem] py-[1rem]"
                        >
                          <div class="flex h-[1.5em] items-center">
                            <div [class]="skeletonBarClass + ' h-[0.75rem] ' + widthClass"></div>
                          </div>
                        </td>
                      </tr>
                    </ng-container>

                    <ng-container *ngIf="!loading">
                      <tr
                        *ngFor="let item of historic"
                        class="cursor-pointer border-b border-l-0 border-r-0 border-t-0 border-solid border-aav-outline hover:bg-aav-surface-container-lowest"
                        (click)="viewDetails(item)"
                      >
                        <td class="whitespace-nowrap px-[1.5rem] py-[1rem] text-aav-on-surface">
                          {{ item.date | dateFormat: 'dd/MM/yyyy HH:mm' }}
                        </td>
                        <td class="px-[1.5rem] py-[1rem] text-aav-on-surface">
                          {{ item.entityName || notAvailable }}
                        </td>
                        <td class="px-[1.5rem] py-[1rem] text-aav-on-surface">
                          {{ item.plate || notAvailable }}
                        </td>
                        <td class="px-[1.5rem] py-[1rem] text-aav-on-surface" data-cell="type">
                          {{ item.changeType | historicTypeKey | translate }}
                        </td>
                        <td
                          class="max-w-[180px] truncate px-[1.5rem] py-[1rem] text-aav-on-surface-variant"
                        >
                          {{ item.oldValue || notAvailable }}
                        </td>
                        <td
                          class="max-w-[180px] truncate px-[1.5rem] py-[1rem] text-aav-on-surface-variant"
                        >
                          {{ item.newValue || notAvailable }}
                        </td>
                      </tr>
                      <tr *ngIf="historic.length === 0">
                        <td colspan="6" class="px-[1.5rem] py-16 text-center" data-state="empty">
                          <div class="text-aav-outline-variant text-4xl mb-[0.75rem]">📋</div>
                          <p class="text-aav-slate-400 text-sm">
                            {{ 'VEHICLE_HISTORIC.EMPTY_STATE.TITLE' | translate }}
                          </p>
                          <p class="text-aav-outline-variant text-xs mt-1">
                            {{ 'VEHICLE_HISTORIC.EMPTY_STATE.SUBTITLE' | translate }}
                          </p>
                        </td>
                      </tr>
                    </ng-container>
                  </tbody>
                </table>
              </div>

              <!-- Rodape da tabela: contador e itens por pagina a esquerda, paginacao a
                   direita. O flex-none nos dois lados neutraliza o .flex{flex:1} de
                   assets/css/angular-material.min.css, que capturaria a classe flex do
                   Tailwind e faria os blocos crescerem sozinhos dentro deste container. -->
              <div
                class="flex flex-wrap items-center justify-between gap-4 border-b-0 border-l-0 border-r-0 border-t border-solid border-aav-neutral-200 px-[1.5rem] py-[1rem]"
                data-block="table-footer"
              >
                <div
                  *ngIf="loading"
                  data-state="loading"
                  class="flex flex-none flex-wrap items-center gap-4"
                >
                  <div [class]="skeletonBarClass + ' h-[0.75rem] w-[11rem]'"></div>
                  <div [class]="skeletonBarClass + ' h-[0.75rem] w-[7rem]'"></div>
                  <div [class]="skeletonPageSizeClass"></div>
                </div>

                <div *ngIf="!loading" class="flex flex-none flex-wrap items-center gap-4">
                  <!-- innerHTML porque o layout aprovado pede os numeros em negrito e a chave
                       traz <strong> no proprio valor; a sanitizacao do Angular permanece
                       ativa e os unicos valores interpolados sao numeros. -->
                  <span
                    class="text-[13px] text-aav-slate-400 [&_strong]:text-aav-on-surface"
                    data-info="showing-records"
                    [innerHTML]="
                      'VEHICLE_HISTORIC.SHOWING_RECORDS'
                        | translate: { shown: historic.length, total: totalItems }
                    "
                  ></span>

                  <label
                    class="m-0 flex flex-none items-center gap-2 text-[13px] text-aav-on-surface-variant"
                  >
                    {{ 'VEHICLE_HISTORIC.FILTER_BOX.ITEMS_PER_PAGE' | translate }}
                    <span class="relative flex items-center">
                      <select
                        [formControl]="pageSizeControl"
                        data-input="page-size"
                        class="h-[30px] w-16 cursor-pointer appearance-none rounded-aav-xs border-[1px] border-solid border-aav-neutral-200 bg-aav-surface pl-[0.625rem] pr-[1.5rem] text-[13px] text-aav-on-surface focus:border-aav-primary focus:outline-none"
                      >
                        <option *ngFor="let size of pageSizeOptions" [ngValue]="size">
                          {{ size }}
                        </option>
                      </select>
                      <aav-icon
                        name="expand_more"
                        [size]="15"
                        class="pointer-events-none absolute right-[0.375rem] text-aav-on-surface-variant"
                      ></aav-icon>
                    </span>
                  </label>
                </div>

                <div
                  *ngIf="loading && paginationSkeletonButtons.length > 0"
                  data-state="loading"
                  class="flex flex-none items-center gap-[0.375rem]"
                >
                  <div
                    *ngFor="let button of paginationSkeletonButtons; trackBy: trackBySkeletonRow"
                    [class]="skeletonPageButtonClass"
                  ></div>
                </div>

                <app-pagination
                  *ngIf="!loading && totalPages > 1"
                  class="flex-none"
                  [currentPage]="currentPage"
                  [totalPages]="totalPages"
                  (pageChange)="onPageChange($event)"
                ></app-pagination>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  `,
})
export class VehicleHistoricV2Component implements OnInit {
  readonly columnKeys: ReadonlyArray<string> = [
    'VEHICLE_HISTORIC.COLUMNS_TABLE.DATE',
    'VEHICLE_HISTORIC.COLUMNS_TABLE.ENTITY',
    'VEHICLE_HISTORIC.COLUMNS_TABLE.PLATE',
    'VEHICLE_HISTORIC.COLUMNS_TABLE.TYPE',
    'VEHICLE_HISTORIC.COLUMNS_TABLE.OLD_VALUE',
    'VEHICLE_HISTORIC.COLUMNS_TABLE.NEW_VALUE',
  ];

  get notAvailable(): string {
    return this.translateService?.instant('GENERIC.NOT_AVAILABLE') ?? 'N/A';
  }

  historic: VehicleHistoricEntry[] = [];
  entities: Entity[] = [];

  // Referencia estavel: o *ngFor do aav-listbox recria os <li> quando o array muda de
  // identidade, e um array novo a cada ciclo de deteccao apagava a opcao entre o mousedown
  // e o mouseup, impedindo o clique de selecionar.
  entityOptions: SelectOption[] = [];

  filterForm = new FormGroup({
    date: new FormControl<string>(''),
    entityId: new FormControl<string>('', { nonNullable: true }),
    plate: new FormControl<string>(''),
  });

  currentPage: number = 1;
  totalItems: number = 0;
  totalPages: number = 0;

  readonly pageSizeOptions = PAGE_SIZE_OPTIONS;
  readonly RESULTS_CARD_CLASS = RESULTS_CARD_CLASS;
  readonly skeletonBarClass = SKELETON_BAR_CLASS;
  readonly skeletonFieldClass = SKELETON_FIELD_CLASS;
  readonly skeletonSubmitClass = SKELETON_SUBMIT_CLASS;
  readonly skeletonPageSizeClass = SKELETON_PAGE_SIZE_CLASS;
  readonly skeletonPageButtonClass = SKELETON_PAGE_BUTTON_CLASS;

  paginationSkeletonButtons: ReadonlyArray<number> = [];
  readonly skeletonCellWidthClasses = SKELETON_CELL_WIDTH_CLASSES;

  skeletonRows: ReadonlyArray<number> = buildSkeletonRows(DEFAULT_PAGE_SIZE);

  pageSizeControl = new FormControl<number>(DEFAULT_PAGE_SIZE, { nonNullable: true });

  loading: boolean = false;

  // Cobre o carregamento inicial da tela inteira (cabecalho, filtros e tabela) e cai junto
  // com o primeiro `loading`; buscas seguintes so trocam a tabela. Nao espera
  // `loadEntities()`: a lista de lojas so alimenta o combo do filtro, entao segurar a tela
  // inteira por ela atrasaria o conteudo que ja esta pronto.
  initialLoading: boolean = true;

  private historyService: VehicleChangesHistoryService | null = null;
  private dialogService: DialogService | null = null;
  private toastService: ToastService | null = null;
  private translateService: TranslateService | null = null;

  ngOnInit(): void {
    this.historyService = getAngularService<VehicleChangesHistoryService>(
      VehicleChangesHistoryService
    );
    this.dialogService = getAngularService<DialogService>(DialogService);
    this.toastService = getAngularService<ToastService>(ToastService);
    this.translateService = getAngularService<TranslateService>(TranslateService);

    this.pageSizeControl.valueChanges.subscribe(() => this.onPageSizeChange());

    this.buildEntityOptions();

    this.loadEntities();
    this.loadHistoric();
  }

  private buildEntityOptions(): void {
    const all = this.translateService?.instant('VEHICLE_HISTORIC.FILTER_BOX.ALL_ENTITIES') ?? '';
    this.entityOptions = [
      { value: '', label: all },
      ...this.entities.map((entity) => ({ value: String(entity.id), label: entity.name })),
    ];
  }

  get pageSize(): number {
    return this.pageSizeControl.value;
  }

  onPageSizeChange(): void {
    this.currentPage = 1;
    this.skeletonRows = buildSkeletonRows(this.pageSize);
    this.loadHistoric();
  }

  trackBySkeletonRow(index: number): number {
    return index;
  }

  trackBySkeletonCell(index: number): number {
    return index;
  }

  async loadEntities(): Promise<void> {
    if (!this.historyService) return;

    try {
      this.entities = await this.historyService.getEntities();
      this.buildEntityOptions();
    } catch {
      this.entities = [];
      this.buildEntityOptions();
      this.notifyError('VEHICLE_HISTORIC.ERROR.LOAD_LIST');
    }
  }

  async loadHistoric(): Promise<void> {
    if (!this.historyService) return;

    this.loading = true;

    try {
      const { date, entityId, plate } = this.filterForm.value;
      const filter: VehicleHistoricFilter = {
        date: date || undefined,
        entityId: entityId ? Number(entityId) : undefined,
        plate: plate ? plate.toUpperCase() : undefined,
      };

      const pagination: Pagination = {
        page: this.currentPage,
        limit: this.pageSize,
      };

      const result = await this.historyService.getVehicleHistoricList(pagination, filter);

      this.historic = result.rows ?? [];
      this.updateTotals(result.count ?? 0);
    } catch {
      this.historic = [];
      this.updateTotals(0);
      this.notifyError('VEHICLE_HISTORIC.ERROR.LOAD_LIST');
    } finally {
      this.loading = false;
      this.initialLoading = false;
    }
  }

  search(): void {
    this.currentPage = 1;
    this.loadHistoric();
  }

  onPageChange(page: number): void {
    if (page === this.currentPage) return;

    this.currentPage = page;
    this.loadHistoric();
  }

  viewDetails(item: VehicleHistoricEntry): void {
    if (!this.dialogService) return;
    if (!DETAILABLE_CHANGE_TYPES.has(toChangeTypeCode(item.changeType))) return;

    this.dialogService.open(VEHICLE_HISTORY_DETAILS_DIALOG_SELECTOR, {
      vehicleId: item.vehicleId,
      changeDate: item.date,
      entityName: item.entityName,
      changeType: item.changeType,
      oldValue: item.oldValue,
      newValue: item.newValue,
      versionName: item.versionName,
    });
  }

  private updateTotals(totalItems: number): void {
    this.totalItems = totalItems;
    this.totalPages = Math.ceil(totalItems / this.pageSize);
    this.paginationSkeletonButtons = buildPaginationSkeletonButtons(this.totalPages);
  }

  private notifyError(key: string): void {
    this.toastService?.error(this.translateService?.instant(key) ?? key);
  }
}
